import { Injectable } from '@angular/core';
import type { ComponenteModyo } from '@core/interfaces/pagina-modyo.interface';
import type {
  ModyoEntry,
  LogoFields,
  IconoFields,
  ParrafoFields,
  BotonFields,
  CheckboxRadioFields,
  TarjetaFields,
  HeaderFields,
  BodyFields,
  ItemFields,
} from '@core/interfaces/modyo';

@Injectable({
  providedIn: 'root',
})
export class ServicioAdaptadorModyo {
  public entryAComponenteModyo(entry: ModyoEntry<unknown>): ComponenteModyo {
    const fields = entry.fields as Record<string, unknown>;

    const componente = {
      tipo: this.obtenerTipoDeCategoria(entry.meta.category ?? undefined),
      tipoComponente: entry.meta.type,
      propiedades: {
        ...fields,
        uuid: entry.meta.uuid,
        slug: entry.meta.slug,
        name: entry.meta.name,
      },
    };

    return componente;
  }

  public componenteModyoAEntry(componente: ComponenteModyo): ModyoEntry<unknown> {
    const { uuid, slug, name, ...fields } = componente.propiedades;

    return {
      meta: {
        uuid: (uuid as string) || '',
        slug: (slug as string) || '',
        name: (name as string) || componente.tipoComponente,
        type: componente.tipoComponente,
        category: this.obtenerCategoriaDelTipo(componente.tipo),
        tags: [],
        space: '',
        locale: 'es',
        excerpt: '',
        private: false,
        targets: [],
        created_at: '',
        updated_at: '',
        published_at: '',
        unpublish_at: null,
        version_type: 'current',
        category_name: this.capitalize(componente.tipo),
        category_slug: componente.tipo,
        available_locales: ['es'],
        author: {},
      },
      fields,
    };
  }

  public esComponenteValido(componente: unknown): componente is ComponenteModyo {
    return (
      typeof componente === 'object' &&
      componente !== null &&
      'tipo' in componente &&
      'tipoComponente' in componente &&
      'propiedades' in componente
    );
  }

  public extraerFieldsTipados<T>(entry: ModyoEntry<unknown>): T {
    return entry.fields as T;
  }

  public esLogo(entry: ModyoEntry<unknown>): entry is ModyoEntry<LogoFields> {
    return entry.meta.type === 'campo-entrada';
  }

  public esIcono(entry: ModyoEntry<unknown>): entry is ModyoEntry<IconoFields> {
    return entry.meta.type === 'icon';
  }

  public esParrafo(entry: ModyoEntry<unknown>): entry is ModyoEntry<ParrafoFields> {
    return entry.meta.type === 'textoenriquecido';
  }

  public esBoton(entry: ModyoEntry<unknown>): entry is ModyoEntry<BotonFields> {
    return entry.meta.type === 'boton';
  }

  public esCheckbox(entry: ModyoEntry<unknown>): entry is ModyoEntry<CheckboxRadioFields> {
    return entry.meta.type === 'radio-checkbox';
  }

  public esTarjeta(entry: ModyoEntry<unknown>): entry is ModyoEntry<TarjetaFields> {
    return entry.meta.type === 'tarjeta';
  }

  public esHeader(entry: ModyoEntry<unknown>): entry is ModyoEntry<HeaderFields> {
    return entry.meta.type === 'header';
  }

  public esBody(entry: ModyoEntry<unknown>): entry is ModyoEntry<BodyFields> {
    return entry.meta.type === 'body';
  }

  public esItem(entry: ModyoEntry<unknown>): entry is ModyoEntry<ItemFields> {
    const resultado = entry.meta.type === 'item';
    return resultado;
  }

  private obtenerTipoDeCategoria(category?: string): ComponenteModyo['tipo'] {
    const categoryMap: Record<string, ComponenteModyo['tipo']> = {
      atomos: 'atomo',
      moleculas: 'molecula',
      organismos: 'organismo',
      plantillas: 'plantilla',
      paginas: 'pagina',
    };

    return categoryMap[category ?? ''] ?? 'atomo';
  }

  private obtenerCategoriaDelTipo(tipo: ComponenteModyo['tipo']): string {
    const tipoMap: Record<ComponenteModyo['tipo'], string> = {
      atomo: 'atomos',
      molecula: 'moleculas',
      organismo: 'organismos',
      plantilla: 'plantillas',
      pagina: 'paginas',
    };

    return tipoMap[tipo];
  }

  private capitalize(str: string): string {
    const firstCharIndex = 0;
    const afterFirstCharIndex = 1;
    return str.charAt(firstCharIndex).toUpperCase() + str.slice(afterFirstCharIndex);
  }
}
